import { Button } from '@progress/kendo-react-buttons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDroppable } from '@dnd-kit/core';
import { useRef } from 'react';
import type { KanbanColumnProps } from './kanban-column.types.ts';
import styles from './kanban-column.module.scss';

export function KanbanColumn({ id, title, collapsed, data = [], renderCard, onToggleView }: KanbanColumnProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { setNodeRef, isOver } = useDroppable({
    id: id,
  });

  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 170,
    gap: 12,
    overscan: 3,
  });

  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        className={`${styles.container} ${isOver ? styles.isOver : ''} ${styles.collapsed}`}
        onClick={() => onToggleView(id)}
      >
        <div className={styles.collapsedStrip}>
          <Button
            className={styles.headerBtn}
            onClick={(e) => {
              e.stopPropagation();
              onToggleView(id);
            }}
            aria-label="Expand column"
          >
            <FontAwesomeIcon icon={faEye} />
          </Button>
          <div className={styles.collapsedTitleGroup}>
            <span className={styles.collapsedTitle}>{title}</span>
            <span className={styles.collapsedBadge}>{data.length}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} className={`${styles.container} ${isOver ? styles.isOver : ''}`}>
      <div className={styles.header}>
        <div className={styles.headerTitleGroup}>
          <span className={styles.headerTitle} title={title}>
            {title}
          </span>
          <span className={styles.headerBadge}>{data.length}</span>
        </div>
        <Button className={styles.headerBtn} onClick={() => onToggleView(id)} aria-label="Hide column">
          <FontAwesomeIcon icon={faEyeSlash} />
        </Button>
      </div>
      <div ref={scrollContainerRef} className={styles.body}>
        <div className={styles.virtualContainer} style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              className={styles.virtualItem}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {renderCard({ data: data[virtualRow.index] })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
